// Converte áudio PCM cru capturado no navegador para WAV PCM 16-bit mono 16 kHz.
// O Gemini aceita WAV/MP3/AIFF/AAC/OGG/FLAC — NÃO aceita webm.

/**
 * Converte PCM cru (mono) para WAV 16 kHz 16 bits.
 *
 * Quando a captura já vem a 16 kHz — o caso comum, porque o AudioContext é criado pedindo essa
 * taxa — não há resample nenhum: só o encode. O caminho de resample existe para os browsers que
 * recusam `sampleRate: 16000` e entregam a taxa nativa.
 */
export async function float32ToWav16k(
  amostras: Float32Array,
  taxaOrigem: number,
): Promise<Blob> {
  const destino = 16000;
  if (amostras.length === 0) return encodeWav(new Float32Array(0), destino);
  if (taxaOrigem === destino) return encodeWav(amostras, destino);

  const frames = Math.max(1, Math.round((amostras.length * destino) / taxaOrigem));
  const offline = new OfflineAudioContext(1, frames, destino);
  const origem = offline.createBuffer(1, amostras.length, taxaOrigem);
  origem.getChannelData(0).set(amostras);

  const src = offline.createBufferSource();
  src.buffer = origem;
  src.connect(offline.destination);
  src.start(0);

  const renderizado = await offline.startRendering();
  return encodeWav(renderizado.getChannelData(0), destino);
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);          // Subchunk1Size (PCM)
  view.setUint16(20, 1, true);           // AudioFormat = PCM
  view.setUint16(22, 1, true);           // channels = mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byteRate = rate * channels * 2
  view.setUint16(32, 2, true);           // blockAlign
  view.setUint16(34, 16, true);          // bitsPerSample
  writeStr(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let off = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    off += 2;
  }
  return new Blob([view], { type: 'audio/wav' });
}
